import React from 'react';
import { useIntl } from 'react-intl';
import type { UserProfileData } from '@contexts/AuthContextStore';
import LabeledSelect from '../fields/LabeledSelect';
import LabeledTextInput from '../fields/LabeledTextInput';
import {
  COMPANY_SIZE_OPTIONS,
  INDUSTRY_OPTIONS,
  ROLE_OPTIONS,
} from '../options';
import {
  FieldStack,
  StepSubtitle,
  StepTitle,
  TwoColRow,
} from '../Onboarding.styled';

interface StepAboutYouProps {
  data: UserProfileData;
  errors: Partial<Record<keyof UserProfileData, string>>;
  update: (patch: Partial<UserProfileData>) => void;
}

const StepAboutYou: React.FC<StepAboutYouProps> = ({ data, errors, update }) => {
  const intl = useIntl();
  return (
    <div data-testid="onboarding-step-aboutYou">
      <StepTitle>
        {intl.formatMessage({ id: 'onboarding.aboutYou.title' })}
      </StepTitle>
      <StepSubtitle>
        {intl.formatMessage({ id: 'onboarding.aboutYou.subtitle' })}
      </StepSubtitle>

      <FieldStack>
        <TwoColRow>
          <LabeledTextInput
            id="firstName"
            label={intl.formatMessage({ id: 'onboarding.aboutYou.firstName' })}
            placeholder={intl.formatMessage({
              id: 'onboarding.aboutYou.firstName.placeholder',
            })}
            value={data.firstName ?? ''}
            onChange={(v) => update({ firstName: v })}
            error={errors.firstName}
            required
          />
          <LabeledTextInput
            id="lastName"
            label={intl.formatMessage({ id: 'onboarding.aboutYou.lastName' })}
            placeholder={intl.formatMessage({
              id: 'onboarding.aboutYou.lastName.placeholder',
            })}
            value={data.lastName ?? ''}
            onChange={(v) => update({ lastName: v })}
            error={errors.lastName}
            required
          />
        </TwoColRow>

        <LabeledTextInput
          id="companyName"
          label={intl.formatMessage({ id: 'onboarding.aboutYou.companyName' })}
          placeholder={intl.formatMessage({
            id: 'onboarding.aboutYou.companyName.placeholder',
          })}
          value={data.companyName ?? ''}
          onChange={(v) => update({ companyName: v })}
          error={errors.companyName}
          required
        />

        <LabeledSelect
          id="role"
          label={intl.formatMessage({ id: 'onboarding.aboutYou.role' })}
          placeholder={intl.formatMessage({
            id: 'onboarding.aboutYou.role.placeholder',
          })}
          options={ROLE_OPTIONS}
          value={data.role ?? ''}
          onChange={(v) => update({ role: v })}
          error={errors.role}
          required
        />

        <TwoColRow>
          <LabeledSelect
            id="industry"
            label={intl.formatMessage({ id: 'onboarding.aboutYou.industry' })}
            placeholder={intl.formatMessage({
              id: 'onboarding.aboutYou.industry.placeholder',
            })}
            options={INDUSTRY_OPTIONS}
            value={data.industry ?? ''}
            onChange={(v) => update({ industry: v })}
            error={errors.industry}
            required
          />
          <LabeledSelect
            id="companySize"
            label={intl.formatMessage({ id: 'onboarding.aboutYou.companySize' })}
            placeholder={intl.formatMessage({
              id: 'onboarding.aboutYou.companySize.placeholder',
            })}
            options={COMPANY_SIZE_OPTIONS}
            value={data.companySize ?? ''}
            onChange={(v) => update({ companySize: v })}
            error={errors.companySize}
            required
          />
        </TwoColRow>
      </FieldStack>
    </div>
  );
};

export default StepAboutYou;
